/**
 * LanguageSwitcher - KEIKKA v8.0
 * 
 * World-class language selector showing ALL supported languages.
 * All languages have complete translation files - NO leaks.
 * 
 * Features:
 * - Shows 7 supported languages (ET, EN, FI, SV, NB, RU, ES)
 * - Visual flag indicators
 * - Cross-tab sync on selection
 * - Mobile-friendly design
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useLanguage } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Globe, Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  SUPPORTED_LOCALES, 
  searchLanguages,
  getLocaleInfo 
} from '@/i18n';

export function LanguageSwitcher({ variant = 'default', className }) {
  const { language, setLanguage, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);

  // Current language info
  const currentLang = useMemo(() => {
    return getLocaleInfo(language);
  }, [language]);

  // Filtered languages
  const filteredLanguages = useMemo(() => {
    let langs = searchQuery.trim() 
      ? searchLanguages(searchQuery) 
      : SUPPORTED_LOCALES;
    
    // Sort: current language first, then alphabetically by nativeLabel
    return [...langs].sort((a, b) => {
      if (a.code === language) return -1;
      if (b.code === language) return 1;
      return a.nativeLabel.localeCompare(b.nativeLabel);
    });
  }, [searchQuery, language]);

  // Handle language selection
  const handleSelect = useCallback((code) => {
    setLanguage(code);
    setIsOpen(false);
    setSearchQuery('');
  }, [setLanguage]);

  // Focus search on open
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset search on close
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Render language item
  const renderLanguageItem = (lang) => {
    const isSelected = lang.code === language;
    
    return (
      <button
        key={lang.code}
        onClick={() => handleSelect(lang.code)}
        className={cn(
          "w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left transition-all",
          "hover:bg-accent focus:bg-accent focus:outline-none",
          isSelected && "bg-primary/10 ring-1 ring-primary/30"
        )}
        data-testid={`language-option-${lang.code}`}
        aria-selected={isSelected}
      >
        {/* Flag */}
        <span className="text-xl flex-shrink-0" aria-hidden="true">
          {lang.flag}
        </span>
        
        {/* Name */}
        <div className="flex-1 min-w-0">
          <span className={cn("font-medium", isSelected && "text-primary")}>
            {lang.nativeLabel}
          </span>
          <span className="text-xs text-muted-foreground ml-2">
            {lang.label}
          </span>
        </div>
        
        {/* Check mark */}
        {isSelected && (
          <Check className="h-4 w-4 text-primary flex-shrink-0" />
        )}
      </button>
    );
  };

  // Compact variant for header
  if (variant === 'compact') {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn("gap-1.5 px-2", className)}
            data-testid="language-switcher-compact"
            aria-label={t('common.language') || 'Language'}
          >
            <span className="text-base">{currentLang.flag}</span>
            <span className="hidden sm:inline text-sm">{currentLang.nativeLabel}</span>
            <ChevronDown className="h-3 w-3 opacity-60" />
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[380px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              {t('languageSwitcher.selectLanguage') || 'Select Language'}
            </DialogTitle>
            <DialogDescription>
              {t('languageSwitcher.choosePreferred') || 'Choose your preferred language'}
            </DialogDescription>
          </DialogHeader>
          
          {/* Search */}
          <div className="relative mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              placeholder={t('languageSwitcher.searchLanguages') || 'Search languages...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="language-search-input"
            />
          </div>
          
          {/* Languages list */}
          <ScrollArea className="h-[320px] mt-2">
            <div className="space-y-0.5 pr-3">
              {filteredLanguages.length > 0 ? (
                filteredLanguages.map(renderLanguageItem)
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  {t('common.noResults') || 'No results'}
                </p>
              )}
            </div>
          </ScrollArea>
          
          {/* Footer */}
          <p className="text-xs text-muted-foreground text-center mt-2">
            {SUPPORTED_LOCALES.length} {t('languageSwitcher.languagesAvailable') || 'languages available'}
          </p>
        </DialogContent>
      </Dialog>
    );
  }

  // Default full variant
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className={cn("gap-2", className)}
          data-testid="language-switcher"
          aria-label={t('common.language') || 'Language'}
        >
          <Globe className="h-4 w-4" />
          <span className="text-lg">{currentLang.flag}</span>
          <span>{currentLang.nativeLabel}</span>
          <ChevronDown className="h-4 w-4 opacity-60" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            {t('languageSwitcher.selectLanguage') || 'Select Language'}
          </DialogTitle>
          <DialogDescription>
            {t('languageSwitcher.choosePreferred') || 'Choose your preferred language'}
          </DialogDescription>
        </DialogHeader>
        
        {/* Languages list */}
        <ScrollArea className="h-[380px] mt-3">
          <div className="space-y-0.5 pr-3">
            {filteredLanguages.map(renderLanguageItem)}
          </div>
        </ScrollArea>
        
        {/* Footer */}
        <div className="text-xs text-muted-foreground text-center mt-3 pt-3 border-t">
          <p>{SUPPORTED_LOCALES.length} {t('languageSwitcher.languagesAvailable') || 'languages available'}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default LanguageSwitcher;
